import api from './api';

/**
 * Shared task analytics integration for admin, superadmin, and employee screens.
 * @see docs/BACKEND_PERFORMANCE_API_PROMPT.md
 */

export const ADMIN_TASK_ANALYTICS = '/admin/tasks/analytics';
export const SUPERADMIN_TASK_ANALYTICS = '/superadmin/tasks/analytics';
export const EMPLOYEE_MY_PERFORMANCE = '/employee/tasks/my-performance';

/**
 * Prefer backend { message } (e.g. 400 invalid frequency filter / employeeId).
 */
export function getTaskAnalyticsErrorMessage(err, fallback = 'Could not load performance data.') {
  const data = err?.response?.data;
  const msg = data?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  return fallback;
}

function analyticsUrlsForVariant(variant) {
  if (variant === 'superadmin') {
    return [SUPERADMIN_TASK_ANALYTICS, ADMIN_TASK_ANALYTICS];
  }
  return [ADMIN_TASK_ANALYTICS];
}

/**
 * GET /admin/tasks/analytics or GET /superadmin/tasks/analytics
 * @param {'admin'|'superadmin'} variant
 * @param {URLSearchParams} searchParams — optional `cadence`, employeeId (query param name unchanged on server)
 */
export async function fetchAdminOrSuperadminTaskAnalytics(variant, searchParams) {
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const urls = analyticsUrlsForVariant(variant);
  let lastError;
  for (let i = 0; i < urls.length; i += 1) {
    const url = `${urls[i]}${suffix}`;
    try {
      const res = await api.get(url);
      return { data: res.data || {}, urlUsed: urls[i] };
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      const tryNext =
        variant === 'superadmin' &&
        i < urls.length - 1 &&
        (status === 404 || status === 401 || status === 403);
      if (tryNext) continue;
      throw err;
    }
  }
  throw lastError;
}

/**
 * GET /employee/tasks/my-performance
 * @param {URLSearchParams} searchParams — optional `cadence` query param
 */
export async function fetchEmployeeMyPerformance(searchParams) {
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const res = await api.get(`${EMPLOYEE_MY_PERFORMANCE}${suffix}`);
  return res.data || {};
}

/** Backend may return filtersApplied or filterApplied (employee endpoint). */
export function pickFiltersApplied(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return payload.filtersApplied ?? payload.filterApplied ?? null;
}
